import type { CSSProperties, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import StationLogo from "../assets/img/new-theme/station-logo.png";
import { Back } from "../compoment/icon";
import {
  readActiveStoredStation,
  type StoredStationRecord,
} from "../utils/stationFlowStorage";
import {
  resolveStationThemeBackground,
  resolveStationThemeLogo,
} from "../utils/stationTheme";
import { markNavigationBack } from "../utils/navigationDirection";

type StationThemeLayoutProps = {
  children: ReactNode;
  className?: string;
  /** Optional station override; defaults to active stored station/category. */
  station?: StoredStationRecord | null;
  /** Force a specific background (e.g. FIFA). */
  backgroundOverride?: string | null;
  showStationLogo?: boolean;
  /** Force a specific logo; defaults to the selected art logo. */
  logoOverride?: string | null;
  /** Circular back control. Defaults to true. */
  showBack?: boolean;
  onBack?: () => void;
  /** When false, do not apply the station theme background image. */
  useThemeBackground?: boolean;
  style?: CSSProperties;
};

const StationThemeLayout = ({
  children,
  className = "",
  station,
  backgroundOverride,
  showStationLogo = false,
  logoOverride,
  showBack = true,
  onBack,
  useThemeBackground = true,
  style,
}: StationThemeLayoutProps) => {
  const navigate = useNavigate();
  const activeStation = station ?? readActiveStoredStation();
  const backgroundImage = useThemeBackground
    ? backgroundOverride?.trim() || resolveStationThemeBackground(activeStation)
    : backgroundOverride?.trim() || "";
  const logoImage =
    logoOverride?.trim() || resolveStationThemeLogo() || StationLogo;

  const handleBack = () => {
    markNavigationBack();
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  return (
    <section
      className={`stationThemeLayout ${className}`.trim()}
      style={{
        backgroundImage: backgroundImage
          ? `url("${backgroundImage}")`
          : undefined,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        ...style,
      }}
    >
      {showBack && (
        <button
          type="button"
          className="backBTN chooseOptionBack border-0"
          aria-label="Back"
          onClick={handleBack}
        >
          <Back />
        </button>
      )}
      {showStationLogo && (
        <div className="stationLogoWrap">
          <img
            src={logoImage}
            alt="Station logo"
            className="stationLogo"
          />
        </div>
      )}
      {children}
    </section>
  );
};

export default StationThemeLayout;
